import { useEffect } from "react";
import { SITE } from "@/data/site";

const metaMap = [
  { attr: "name", key: "description" },
  { attr: "property", key: "og:title" },
  { attr: "property", key: "og:description" },
  { attr: "property", key: "og:type" },
  { attr: "property", key: "og:url" },
  { attr: "property", key: "og:image" },
  { attr: "property", key: "og:site_name" },
  { attr: "name", key: "twitter:card" },
  { attr: "name", key: "twitter:title" },
  { attr: "name", key: "twitter:description" },
  { attr: "name", key: "twitter:image" },
];

function updateMeta(attr, key, value) {
  if (!value) return;
  const selector = `meta[${attr}='${key}']`;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", value);
}

function updateLink(rel, href) {
  if (!href) return;
  let element = document.head.querySelector(`link[rel='${rel}']`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function updateJsonLd(data) {
  if (!data) return;
  let script = document.head.querySelector("script[type='application/ld+json'][data-seo='true']");
  if (!script) {
    script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.dataset.seo = "true";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data, null, 2);
}

function getCanonicalUrl(url) {
  const baseUrl = SITE.siteUrl || window.location.origin;
  if (!url) return baseUrl;
  const absolute = url.startsWith("http") ? url : `${baseUrl.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
  return encodeURI(absolute);
}

export function buildLocalBusinessSchema({ url, image }) {
  const addr = { "@type": "PostalAddress" };
  if (SITE.address.streetAddress) addr.streetAddress = SITE.address.streetAddress;
  if (SITE.address.addressLocality) addr.addressLocality = SITE.address.addressLocality;
  if (SITE.address.addressRegion) addr.addressRegion = SITE.address.addressRegion;
  if (SITE.address.postalCode) addr.postalCode = SITE.address.postalCode;
  if (SITE.address.addressCountry) addr.addressCountry = SITE.address.addressCountry;
  return {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    name: SITE.name,
    url: getCanonicalUrl(url || "/"),
    telephone: SITE.phoneDisplay,
    email: SITE.email,
    address: addr,
    geo: {
      "@type": "GeoCoordinates",
      latitude: SITE.geo.latitude,
      longitude: SITE.geo.longitude,
    },
    description: SITE.description,
    image: getCanonicalUrl(image || SITE.logoLight),
    areaServed: SITE.county,
    openingHours: "Mo-Su 06:00-17:00",
    sameAs: SITE.sameAs,
  };
}

export function buildServiceSchema({ service, url, image }) {
  const business = buildLocalBusinessSchema({ url: "/", image: SITE.logoLight });
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.grid,
    description: service.tagline || service.intro,
    serviceType: service.grid,
    provider: business,
    areaServed: SITE.county,
    url: getCanonicalUrl(url),
    image: getCanonicalUrl(image || service.heroImage || SITE.logoLight),
  };
}

export default function Seo({
  title,
  description,
  url = "/",
  image,
  type = "website",
  schema,
  noindex = false,
}) {
  useEffect(() => {
    document.title = title || SITE.name;

    updateMeta("name", "description", description || SITE.description);
    updateMeta("property", "og:title", title || SITE.name);
    updateMeta("property", "og:description", description || SITE.description);
    updateMeta("property", "og:type", type);
    updateMeta("property", "og:url", getCanonicalUrl(url));
    updateMeta("property", "og:image", getCanonicalUrl(image || SITE.logoLight));
    updateMeta("property", "og:site_name", SITE.name);
    updateMeta("name", "twitter:card", "summary_large_image");
    updateMeta("name", "twitter:title", title || SITE.name);
    updateMeta("name", "twitter:description", description || SITE.description);
    updateMeta("name", "twitter:image", getCanonicalUrl(image || SITE.logoLight));

    if (noindex) {
      updateMeta("name", "robots", "noindex, nofollow");
    }

    updateLink("canonical", getCanonicalUrl(url));
    updateLink("icon", SITE.iconOrange || SITE.logoLight);

    const jsonLd = schema || buildLocalBusinessSchema({ url, image });
    updateJsonLd(jsonLd);
  }, [title, description, url, image, type, schema, noindex]);

  return null;
}
